import React, { useState, useRef } from 'react';
import { Share2, Camera } from 'lucide-react';

const EssentialStorySharingComponent = ({ 
  storyTitle = "My Amazing Story", 
  combinedData = [], 
  halfBookId = "sample-book"
}) => {
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalContent, setModalContent] = useState({ title: '', message: '', type: 'success' });
  const canvasRef = useRef(null);

   // Modal Component
   const Modal = ({ isOpen, onClose, children }) => {
    return (
      <>
        {isOpen && (
          <div className="fixed z-50 inset-0 overflow-y-auto">
            <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
              </div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                {children}
              </div>
            </div>
          </div>
        )}
      </>
    );
  };

  // Show modal with content
  const showSuccessModal = (title, message) => {
    setModalContent({ title, message, type: 'success' });
    setShowModal(true);
  };

  const showErrorModal = (title, message) => {
    setModalContent({ title, message, type: 'error' });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    // If it's a mobile success modal, try to open Instagram
    if (modalContent.type === 'success') {
      try {
        window.location.href = 'instagram://camera';
        // Fallback to Instagram website
        setTimeout(() => {
          window.open('https://www.instagram.com/', '_blank');
        }, 1000);
      } catch (error) {
        console.log('Could not open Instagram app');
      }
    }
  };
  // Generate square Instagram post format for carousel
  const generateInstagramCarouselPage = async (pageIndex = 0) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Instagram carousel format: Square 1080x1080
    canvas.width = 1080;
    canvas.height = 1080;
    
    // Background gradient
    const gradient = ctx.createLinearGradient(0, 0, 0, canvas.height);
    gradient.addColorStop(0, '#667eea');
    gradient.addColorStop(1, '#764ba2');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Add semi-transparent overlay
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    const item = combinedData.length > 0 ? combinedData[pageIndex] : { 
      story: "This is a sample story about a magical adventure where a brave little child discovers a hidden world full of wonder and friendship...", 
      image: null, 
      legend: "" 
    };
    
    // Title (only on first page)
    if (pageIndex === 0) {
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 10;
      
      const titleWords = storyTitle.split(' ');
      let titleLine = '';
      let titleY = 80;
      
      for (let n = 0; n < titleWords.length; n++) {
        const testLine = titleLine + titleWords[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > 900 && n > 0) {
          ctx.fillText(titleLine, canvas.width / 2, titleY);
          titleLine = titleWords[n] + ' ';
          titleY += 60;
        } else {
          titleLine = testLine;
        }
      }
      ctx.fillText(titleLine, canvas.width / 2, titleY);
    }
    
    // Story image (if available)
    const startY = pageIndex === 0 ? 180 : 80;
    
    if (item.image) {
      try {
        const img = new Image();
        await new Promise((resolve, reject) => {
          img.onload = resolve;
          img.onerror = reject;
          img.src = item.image;
        });
        
        // Calculate dimensions to fit nicely
        const maxWidth = 700;
        const maxHeight = pageIndex === 0 ? 450 : 500;
        const ratio = Math.min(maxWidth / img.width, maxHeight / img.height);
        const newWidth = img.width * ratio;
        const newHeight = img.height * ratio;
        
        const x = (canvas.width - newWidth) / 2;
        const y = startY;
        
        // Add white border around image
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(x - 15, y - 15, newWidth + 30, newHeight + 30);
        
        ctx.drawImage(img, x, y, newWidth, newHeight);
        
        // Story text below image
        ctx.fillStyle = '#ffffff';
        ctx.font = '28px Arial';
        ctx.textAlign = 'center';
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 5;
        
        const storyWords = item.story ? item.story.split(' ') : [];
        let line = '';
        let textY = y + newHeight + 60;
        
        for (let n = 0; n < storyWords.length; n++) {
          const testLine = line + storyWords[n] + ' ';
          const metrics = ctx.measureText(testLine);
          const testWidth = metrics.width;
          
          if (testWidth > 900 && n > 0) {
            ctx.fillText(line, canvas.width / 2, textY);
            line = storyWords[n] + ' ';
            textY += 40;
            
            if (textY > 920) {
              ctx.fillText(line + '...', canvas.width / 2, textY);
              break;
            }
          } else {
            line = testLine;
          }
        }
        if (line.trim() && textY <= 920) {
          ctx.fillText(line, canvas.width / 2, textY);
        }
        
      } catch (error) {
        console.error('Error loading image:', error);
      }
    } else {
      // Just story text if no image
      ctx.fillStyle = '#ffffff';
      ctx.font = '32px Arial';
      ctx.textAlign = 'center';
      ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
      ctx.shadowBlur = 5;
      
      const words = item.story ? item.story.split(' ') : [];
      let line = '';
      let y = startY + 100;
      
      for (let n = 0; n < words.length; n++) {
        const testLine = line + words[n] + ' ';
        const metrics = ctx.measureText(testLine);
        const testWidth = metrics.width;
        
        if (testWidth > 900 && n > 0) {
          ctx.fillText(line, canvas.width / 2, y);
          line = words[n] + ' ';
          y += 50;
          
          if (y > 870) {
            ctx.fillText(line + '...', canvas.width / 2, y);
            break;
          }
        } else {
          line = testLine;
        }
      }
      if (line.trim() && y <= 870) {
        ctx.fillText(line, canvas.width / 2, y);
      }
    }
    
    // Page indicator and branding (moved higher to avoid Instagram's slide counter)
    ctx.fillStyle = 'rgba(255, 255, 255, 0.9)';
    ctx.font = '22px Arial';
    ctx.textAlign = 'center';
    ctx.shadowBlur = 0;
    ctx.fillText(`${pageIndex + 1}/${combinedData.length || 1} • MyTaleMaker ✨`, canvas.width / 2, 980);
    
    return new Promise(resolve => {
      canvas.toBlob(resolve, 'image/png', 0.9);
    });
  };

  // Generate all carousel images and download
  const generateInstagramCarousel = async () => {
    // Check if data is available
    if (combinedData.length === 0) {
      alert('⏳ No story data available. Please wait for the story to load!');
      return;
    }

    setIsGeneratingImages(true);
    
    try {
      const totalPages = Math.min(combinedData.length, 10); // Instagram allows max 10 slides
      
      // Detect if user is on mobile
     // const isMobile = /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      
      for (let i = 0; i < totalPages; i++) {
        const blob = await generateInstagramCarouselPage(i);
        const url = URL.createObjectURL(blob);
        
        // Zero-padded naming for proper sorting (01, 02, 03, etc.)
        const paddedIndex = String(i + 1).padStart(2, '0');
        const fileName = `${storyTitle.replace(/\s+/g, '_')}_slide_${paddedIndex}_of_${String(totalPages).padStart(2, '0')}.png`;
        
        // Download each image with proper delay
        await new Promise(resolve => {
          setTimeout(() => {
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName;
            a.click();
            URL.revokeObjectURL(url);
            resolve();
          }, i * 1000); // 1 second delay between each download
        });
      }
      //alert(`✅ Downloaded ${totalPages} carousel slides!\n\n📁 Files saved to Downloads folder:\n- slide_01_of_${String(totalPages).padStart(2, '0')}.png\n- slide_02_of_${String(totalPages).padStart(2, '0')}.png\n- etc.\n\n📱 To share on Instagram:\n1. Transfer files to your phone\n2. Open Instagram app\n3. Tap + to create new post\n4. Select files in order (01, 02, 03...)\n5. Add caption and share!\n\n💡 Tip: Use AirDrop, Google Drive, or email to transfer to phone.`);
      showSuccessModal(
        '🎉 Carousel Created Successfully!',
        `Downloaded ${totalPages} carousel slides!\n\n📱 To share on Instagram:\n1. Open Instagram app\n2. Tap + to create new post\n3. Select files in order (01, 02, 03...)\n4. Add caption and share!\n\n`
      );
      // Provide different instructions based on device
    {/*}  if (isMobile) {
        alert(`✅ Downloaded ${totalPages} carousel slides!\n\n📱 Images are saved to your device.\n\nTo share on Instagram:\n1. Open Instagram app\n2. Tap + to create new post\n3. Select files in order (01, 02, 03...)\n4. Add caption and share!`);
        
        // Auto-attempt to open Instagram app
        try {
          window.location.href = 'instagram://camera';
          // Fallback to Instagram website
          setTimeout(() => {
            window.open('https://www.instagram.com/', '_blank');
          }, 1000);
        } catch (error) {
          console.log('Could not open Instagram app');
        }
      } else {
        alert(`✅ Downloaded ${totalPages} carousel slides!\n\n📁 Files saved to Downloads folder:\n- slide_01_of_${String(totalPages).padStart(2, '0')}.png\n- slide_02_of_${String(totalPages).padStart(2, '0')}.png\n- etc.\n\n📱 To share on Instagram:\n1. Transfer files to your phone\n2. Open Instagram app\n3. Tap + to create new post\n4. Select files in order (01, 02, 03...)\n5. Add caption and share!\n\n💡 Tip: Use AirDrop, Google Drive, or email to transfer to phone.`);
      } */}
      
    } catch (error) {
      console.error('Error generating carousel:', error);
      //alert('Error generating carousel images. Please try again.');
      showErrorModal(
        'Generation Failed',
        'Error generating carousel images. Please try again or contact us if the problem persists.'
      );
    }
    
    setIsGeneratingImages(false);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mt-8">
      <div className="flex items-center mb-4">
        <Share2 className="w-6 h-6 text-blue-600 mr-2" />
        <h3 className="text-xl font-bold text-gray-800">Share Your Story</h3>
      </div>
      
      {/* Main Instagram Carousel Option */}
      <div className="mb-6">
        <button
          onClick={generateInstagramCarousel}
          disabled={isGeneratingImages}
          className="w-full flex flex-col items-center p-6 border-2 border-pink-500 bg-gradient-to-r from-pink-50 to-purple-50 rounded-lg hover:from-pink-100 hover:to-purple-100 transition-all disabled:opacity-50 relative"
        >
          <div className="text-pink-500 mb-3">
            <Camera className="w-12 h-12" />
          </div>
          <span className="text-lg font-bold text-gray-800 mb-2">
            {isGeneratingImages ? 'Generating Images...' : 'Instagram Carousel Post'}
          </span>
          <span className="text-sm text-gray-600 text-center max-w-md">
            Share your entire story as a beautiful multi-slide Instagram post. Perfect for showing the complete adventure!
          </span>
          <div className="mt-3 bg-white px-3 py-1 rounded-full text-xs text-gray-600">
            {combinedData.length || 1} slides • Square format • Permanent post
          </div>
        </button>
      </div>

      {/* Hidden canvas for generating images */}
      <canvas
        ref={canvasRef}
        style={{ display: 'none' }}
        width={1080}
        height={1080}
      />
        {/* Modal for messages */}
        <Modal isOpen={showModal} onClose={closeModal}>
        <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div className="sm:flex sm:items-start">
            <div className={`mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full ${modalContent.type === 'success' ? 'bg-green-100' : 'bg-red-100'} sm:mx-0 sm:h-10 sm:w-10`}>
              {modalContent.type === 'success' ? (
                <svg className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : (
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              )}
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left flex-1">
              <h3 className="text-lg leading-6 font-medium text-gray-900 mb-2">
                {modalContent.title}
              </h3>
              <div className="mt-2">
                <p className="text-sm text-gray-500 whitespace-pre-line">
                  {modalContent.message}
                </p>
              </div>
            </div>
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
    {/*}    <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
          <button
            type="button"
            className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:ml-3 sm:w-auto sm:text-sm ${
              modalContent.type === 'success' 
                ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' 
                : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'
            }`}
            onClick={closeModal}
          >
            {modalContent.isMobile ? 'Open Instagram' : 'Done'}
          </button> 
        </div>*/}
      </Modal>

    </div>
  );
};


export default EssentialStorySharingComponent;